// Notification settings for one server, like Discord's (server menu → Notification settings): mute the server, the
// level its channels follow, @everyone, per-channel overrides, and this device's push. What wins (shared/src/notify.ts):
// this device's push off > a level of Nothing > a mute (only @mentions get through) > the channel's level > the server's.
import { useEffect, useState } from "react";
import { muteActive, muteLabel, type Channel, type MuteFor, type NotifyLevel, type ServerNotify } from "@bloom/shared";
import { CloseButton } from "./icons.tsx";
import { BellIcon, DevicePush, LEVEL_OPTIONS, MuteControl } from "./NotifyBell.tsx";
import "./notify.css";

export function ServerNotifySettings({
  serverName,
  server,
  channels,
  levels,
  mutes,
  labelFor,
  onServer,
  onMute,
  onLevel,
  onClose,
}: {
  serverName: string;
  server: ServerNotify;
  /** Text channels and my DMs, in sidebar order. */
  channels: Pick<Channel, "id" | "name" | "kind">[];
  /** My own levels per channel/DM. */
  levels: Record<string, NotifyLevel>;
  mutes: Record<string, number>;
  labelFor: (c: Pick<Channel, "id" | "name" | "kind">) => string;
  onServer: (patch: { level?: NotifyLevel; suppressEveryone?: boolean }) => void;
  /** channelId undefined: the server. */
  onMute: (channelId: string | undefined, f: MuteFor | null) => void;
  onLevel: (channelId: string, level: NotifyLevel | null) => void;
  onClose: () => void;
}) {
  const [adding, setAdding] = useState("");
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [onClose]);

  const level = server.level ?? "mentions";
  // Overrides: channels where I picked a level or that are muted, plus the one being added.
  const overridden = channels.filter((c) => levels[c.id] !== undefined || muteActive(mutes[c.id]) || c.id === adding);
  const addable = channels.filter((c) => !overridden.includes(c));

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal notify-settings" role="dialog" aria-modal="true" aria-labelledby="notify-settings-title">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2 id="notify-settings-title">Notifications for {serverName}</h2>
          <CloseButton onClick={onClose} aria-label="Close" />
        </div>

        <section className="ns-section">
          <MuteControl what={serverName} until={server.mutedUntil} onMute={(f) => onMute(undefined, f)} />
          <p className="ns-note muted">Muting the server covers its channels, not your DMs.</p>
        </section>

        <section className="ns-section" role="radiogroup" aria-labelledby="ns-level">
          <h3 id="ns-level">Server notification settings</h3>
          <p className="ns-note muted">What its channels notify you about, unless you set a channel yourself.</p>
          {LEVEL_OPTIONS.map((o) => (
            <button key={o.level} type="button" role="radio" aria-checked={level === o.level} className={`notify-option${level === o.level ? " on" : ""}`} onClick={() => onServer({ level: o.level })}>
              <BellIcon level={o.level} size={20} />
              <span className="notify-option-text">
                <span className="notify-option-label">
                  {o.label}
                  {o.level === "mentions" && <span className="notify-default">default</span>}
                </span>
                <span className="notify-option-hint">{o.hint}</span>
              </span>
              <span className="notify-check" aria-hidden="true">{level === o.level ? "✓" : ""}</span>
            </button>
          ))}
          <label className="ns-check">
            <input type="checkbox" checked={!!server.suppressEveryone} onChange={(e) => onServer({ suppressEveryone: e.target.checked })} />
            <span>
              <span className="notify-option-label">Suppress @everyone</span>
              <span className="notify-option-hint">@everyone counts as a plain message for you here</span>
            </span>
          </label>
        </section>

        <section className="ns-section">
          <h3>Channel overrides</h3>
          <p className="ns-note muted">Channels and DMs that don't follow the server's setting.</p>
          {overridden.length > 0 && (
            <ul className="ns-overrides">
              {overridden.map((c) => {
                const muted = muteActive(mutes[c.id]);
                return (
                  <li key={c.id} className="ns-override">
                    <span className="ns-override-name">{labelFor(c)}</span>
                    <label className="sr-only" htmlFor={`ns-level-${c.id}`}>Level for {labelFor(c)}</label>
                    <select
                      id={`ns-level-${c.id}`}
                      className="input ns-select"
                      value={levels[c.id] ?? "default"}
                      onChange={(e) => onLevel(c.id, e.target.value === "default" ? null : (e.target.value as NotifyLevel))}
                    >
                      <option value="default">{c.kind === "dm" ? "Default (All messages)" : "Use server default"}</option>
                      {LEVEL_OPTIONS.map((o) => (
                        <option key={o.level} value={o.level}>{o.label}</option>
                      ))}
                    </select>
                    {muted ? (
                      <button type="button" className="link-btn ns-mute" onClick={() => onMute(c.id, null)} title="Unmute">
                        {muteLabel(mutes[c.id]!)} · Unmute
                      </button>
                    ) : (
                      <>
                        <label className="sr-only" htmlFor={`ns-mute-${c.id}`}>Mute {labelFor(c)}</label>
                        <select id={`ns-mute-${c.id}`} className="input ns-select" value="" onChange={(e) => e.target.value && onMute(c.id, e.target.value as MuteFor)}>
                          <option value="">Not muted</option>
                          <option value="24h">Mute 24 hours</option>
                          <option value="1w">Mute 1 week</option>
                          <option value="forever">Mute until I unmute</option>
                        </select>
                      </>
                    )}
                    {(levels[c.id] !== undefined || muted) && (
                      <button
                        type="button"
                        className="link-btn quiet ns-reset"
                        onClick={() => {
                          if (levels[c.id] !== undefined) onLevel(c.id, null);
                          if (muted) onMute(c.id, null);
                          if (adding === c.id) setAdding("");
                        }}
                      >
                        Reset
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {addable.length > 0 && (
            <>
              <label className="sr-only" htmlFor="ns-add">Add a channel override</label>
              <select id="ns-add" className="input ns-select ns-add" value="" onChange={(e) => setAdding(e.target.value)}>
                <option value="">Add a channel or DM…</option>
                {addable.map((c) => (
                  <option key={c.id} value={c.id}>{labelFor(c)}</option>
                ))}
              </select>
            </>
          )}
        </section>

        <section className="ns-section">
          <h3>This device</h3>
          <p className="ns-note muted">Push off here means this device gets nothing from Bloom, whatever the settings above say.</p>
          <DevicePush />
        </section>
      </div>
    </div>
  );
}
